import { arrayMove } from '@dnd-kit/sortable';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type {
  AnalyticalDatasetDefinitionServerSnapshot,
  AnalyticalDatasetDefinitionStoreActions,
  AnalyticalDatasetDefinitionStoreState,
} from './types';
import type { AnalyticalDatasetCohortEntry, AnalyticalDatasetVariableEntry } from '../api/types';

// Element-by-element comparison so add-then-remove (or vice-versa back to
// the original) correctly clears the dirty flag.
const isDirty = (
  cohorts: AnalyticalDatasetCohortEntry[],
  variables: AnalyticalDatasetVariableEntry[],
  snapshot: AnalyticalDatasetDefinitionServerSnapshot | null
): boolean => {
  if (!snapshot) return false;
  if (cohorts.length !== snapshot.cohorts.length) return true;
  if (variables.length !== snapshot.variables.length) return true;
  if (cohorts.some((c, i) => c.cohortProfileId !== snapshot.cohorts[i].cohortProfileId)) {
    return true;
  }
  return variables.some((v, i) => v.avId !== snapshot.variables[i].avId);
};

const initialState: AnalyticalDatasetDefinitionStoreState = {
  loadedVersionId: null,
  cohorts: [],
  variables: [],
  hasStagedChanges: false,
  serverSnapshot: null,
};

export const useAnalyticalDatasetDefinitionStore = create<
  AnalyticalDatasetDefinitionStoreState & AnalyticalDatasetDefinitionStoreActions
>()(
  devtools(
    (set, get) => ({
      ...initialState,
      setFromServer: ({ versionId, definition }) => {
        if (get().loadedVersionId === versionId) return;
        const cohorts = definition?.cohorts ?? [];
        const variables = definition?.variables ?? [];
        set(
          {
            loadedVersionId: versionId,
            cohorts,
            variables,
            hasStagedChanges: false,
            serverSnapshot: { cohorts, variables },
          },
          false,
          'analyticalDatasetDefinition/setFromServer'
        );
      },
      addCohort: (entry) =>
        set(
          (prev) => {
            if (prev.cohorts.some((c) => c.cohortProfileId === entry.cohortProfileId)) return prev;
            const cohorts = [...prev.cohorts, entry];
            return {
              cohorts,
              hasStagedChanges: isDirty(cohorts, prev.variables, prev.serverSnapshot),
            };
          },
          false,
          'analyticalDatasetDefinition/addCohort'
        ),
      removeCohort: (cohortProfileId) =>
        set(
          (prev) => {
            const cohorts = prev.cohorts.filter((c) => c.cohortProfileId !== cohortProfileId);
            return {
              cohorts,
              hasStagedChanges: isDirty(cohorts, prev.variables, prev.serverSnapshot),
            };
          },
          false,
          'analyticalDatasetDefinition/removeCohort'
        ),
      addVariable: (entry) =>
        set(
          (prev) => {
            if (prev.variables.some((v) => v.avId === entry.avId)) return prev;
            const variables = [...prev.variables, entry];
            return {
              variables,
              hasStagedChanges: isDirty(prev.cohorts, variables, prev.serverSnapshot),
            };
          },
          false,
          'analyticalDatasetDefinition/addVariable'
        ),
      removeVariable: (avId) =>
        set(
          (prev) => {
            const variables = prev.variables.filter((v) => v.avId !== avId);
            return {
              variables,
              hasStagedChanges: isDirty(prev.cohorts, variables, prev.serverSnapshot),
            };
          },
          false,
          'analyticalDatasetDefinition/removeVariable'
        ),
      reorderVariables: (activeAvId, overAvId) =>
        set(
          (prev) => {
            const fromIndex = prev.variables.findIndex((v) => v.avId === activeAvId);
            const toIndex = prev.variables.findIndex((v) => v.avId === overAvId);
            if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return prev;

            const variables = arrayMove(prev.variables, fromIndex, toIndex);
            return {
              variables,
              hasStagedChanges: isDirty(prev.cohorts, variables, prev.serverSnapshot),
            };
          },
          false,
          'analyticalDatasetDefinition/reorderVariables'
        ),
      revert: () => {
        const { serverSnapshot } = get();
        if (!serverSnapshot) return;
        set(
          {
            cohorts: serverSnapshot.cohorts,
            variables: serverSnapshot.variables,
            hasStagedChanges: false,
          },
          false,
          'analyticalDatasetDefinition/revert'
        );
      },
      reset: () => set(initialState, false, 'analyticalDatasetDefinition/reset'),
    }),
    { name: 'analyticalDatasetDefinition' }
  )
);
